import React, { useState, useEffect, useMemo, useRef, memo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { getStoreTypeBranding, getPlaceholderImageForCategory } from "../../constants/storeTypes";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Chip,
  Badge,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Slide,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  ThemeProvider,
  createTheme,
  Paper,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Skeleton,
} from "@mui/material";
import {
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  LocalFireDepartment as FireIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import IcecreamIcon from "@mui/icons-material/Icecream";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

// Transition for fullscreen dialog
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Confetti animation component for order completion
const Confetti = ({ show }) => {
  if (!show) return null;

  const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6b9d'];
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    color: colors[Math.floor(Math.random() * colors.length)],
    delay: Math.random() * 0.5,
    size: Math.random() * 8 + 4,
  }));

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {confettiPieces.map((piece) => (
        <motion.div
          key={piece.id}
          initial={{ y: -20, x: `${piece.x}vw`, opacity: 1, rotate: 0 }}
          animate={{
            y: '100vh',
            rotate: Math.random() * 720 - 360,
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: 3,
            delay: piece.delay,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '0%',
          }}
        />
      ))}
    </Box>
  );
};

// Motion wrapper for cards
const MotionCard = motion(Card);

const StoreMenu = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  useTheme();

  // storeId フォールバック（ルートにstoreIdが無い場合のE2E用）
  const effectiveStoreId =
    storeId ||
    (typeof window !== "undefined" &&
      window.env &&
      window.env.FRONTEND_E2E_STORE_ID) ||
    process.env.FRONTEND_E2E_STORE_ID ||
    "b6c4c9f4-3241-4256-9548-cb0c20e8d7e7";

  const location = useLocation();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [orderNote, setOrderNote] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [sessionData, setSessionData] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [orderCompleteOpen, setOrderCompleteOpen] = useState(false);
  const { t, i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [callStaffDialogOpen, setCallStaffDialogOpen] = useState(false);
  const [callReason, setCallReason] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  // 新規追加: UI/UX改善用のstate
  const [searchQuery, setSearchQuery] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  // 初回アニメーション完了フラグ（カート操作時の再アニメーション防止）
  const hasAnimatedRef = useRef(false);

  // Dynamic theme based on store branding (with store type fallback)
  const storeTheme = useMemo(() => {
    // Get store type branding as fallback
    const storeType = store?.store_type || store?.storeType || 'general';
    const typeBranding = getStoreTypeBranding(storeType);

    // Use custom colors if set, otherwise fall back to store type defaults
    const primaryColor = store?.primaryColor || store?.primary_color || typeBranding.colors.primary;
    const secondaryColor = store?.secondaryColor || store?.secondary_color || typeBranding.colors.secondary;
    const accentColor = store?.accentColor || store?.accent_color || typeBranding.colors.accent;

    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: secondaryColor,
        },
        warning: {
          main: accentColor,
        },
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // バックエンドのベースURLを設定
  const baseURL =
    window.env?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000";

  // 商品説明取得用ヘルパー関数
  const getLocalizedDescription = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    const currentLang = i18n.language;
    if (item.description && typeof item.description === "object") {
      return (
        item.description[currentLang] ||
        item.description.ja ||
        item.description.en ||
        item.description.zh ||
        ""
      );
    }
    return "";
  };

  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) {
      return [];
    }

    let result = products;

    // カテゴリフィルタ
    if (selectedCategory !== "all" && selectedCategory) {
      const targetCategoryId = typeof selectedCategory === 'object'
        ? (selectedCategory._id || selectedCategory.id)
        : selectedCategory;

      result = result.filter((product) => {
        const productCategoryId = String(
          product.categoryId ||
          product.category?._id ||
          product.category?.id ||
          product.category
        );
        return productCategoryId === String(targetCategoryId);
      });
    }

    // 検索フィルタ（#17 検索機能）
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((product) => {
        const name = getLocalizedName(product).toLowerCase();
        const desc = getLocalizedDescription(product).toLowerCase();
        return name.includes(query) || desc.includes(query);
      });
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, products, searchQuery, i18n.language]);

  // カート内アイテム総数の計算
  const cartTotalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartItems]);



  // 最近見た商品を更新
  const updateRecentlyViewed = (product) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(p => p._id !== product._id);
      return [product, ...filtered].slice(0, 5);
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // QRコードからはtableとareaのみ取得（sessionIdは自動判定）
    const sessionId = params.get("sessionId"); // 後方互換性のため残す
    const table = params.get("table");
    const area = params.get("area");

    if (table && area) {
      // テーブル情報がある場合はセッション自動判定
      initializeTableSession(table, decodeURIComponent(area), sessionId);
    } else if (sessionId) {
      // 後方互換: sessionIdが直接指定されている場合
      const sessionInfo = {
        sessionId,
        tableNumber: table,
        area: area ? decodeURIComponent(area) : "",
      };
      initializeSession(sessionInfo);
    } else {
      fetchStoreAndMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // テーブルセッションの自動判定・作成
  const initializeTableSession = async (table, area, fallbackSessionId) => {
    try {
      setLoading(true);

      // 既存のアクティブセッションを確認
      const checkResponse = await api.get(
        `/api/public/stores/${effectiveStoreId}/table-session`,
        { params: { table, area } }
      );

      let sessionId;
      if (checkResponse.data?.exists && checkResponse.data?.session) {
        // アクティブなセッションが存在
        sessionId = checkResponse.data.session.id;
        console.log("既存セッションを使用:", sessionId);
      } else {
        // 新しいセッションを作成
        const createResponse = await api.post(
          `/api/public/stores/${effectiveStoreId}/table-session`,
          { table, area }
        );
        sessionId = createResponse.data?.session?.id || fallbackSessionId || crypto.randomUUID();
        console.log("新規セッションを作成:", sessionId);
      }

      localStorage.setItem(
        "current_session",
        JSON.stringify({
          sessionId,
          storeId: effectiveStoreId,
          tableNumber: table,
          area,
        })
      );

      setSessionData({
        sessionId,
        storeId: effectiveStoreId,
        tableNumber: table,
        area,
      });

      await fetchStoreAndMenu();
    } catch (error) {
      console.error("テーブルセッション初期化エラー:", error);
      // フォールバック: 従来の方法で初期化（UUID形式必須）
      const sessionInfo = {
        sessionId: fallbackSessionId || crypto.randomUUID(),
        tableNumber: table,
        area,
      };
      await initializeSession(sessionInfo);
    } finally {
      setLoading(false);
    }
  };

  const initializeSession = async (sessionInfo) => {
    try {
      setLoading(true);

      if (!sessionInfo.sessionId) {
        await fetchStoreAndMenu();
        return;
      }

      localStorage.setItem(
        "current_session",
        JSON.stringify({
          sessionId: sessionInfo.sessionId,
          storeId: effectiveStoreId,
          tableNumber: sessionInfo.tableNumber,
          area: sessionInfo.area,
        })
      );

      setSessionData({
        sessionId: sessionInfo.sessionId,
        storeId: effectiveStoreId,
        tableNumber: sessionInfo.tableNumber,
        area: sessionInfo.area,
      });

      await fetchStoreAndMenu();
    } catch (error) {
      console.error("セッション初期化エラー:", error);
      setError("メニューの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreAndMenu = async () => {
    try {
      const [storeResponse, menuResponse] = await Promise.all([
        api.get(`/api/public/stores/${effectiveStoreId}`),
        api.get(`/api/public/stores/${effectiveStoreId}/menu`),
      ]);

      if (!storeResponse.data || !menuResponse.data) {
        throw new Error("メニューデータの取得に失敗しました");
      }

      const { categories = [], products = [] } = menuResponse.data;

      setStore(storeResponse.data);

      const processedCategories = categories.map((category) => ({
        _id: category._id || category.id,
        name: {
          ja: category.name?.ja || category.name || "",
          en: category.name?.en || "",
          zh: category.name?.zh || "",
        },
        order: category.order || 0,
      }));

      setCategories(processedCategories);

      const processedProducts = products.map((product) => ({
        _id: product._id || product.id,
        id: product.id || product._id,
        categoryId: String(
          product.category_id || product.category?._id || product.category || product.categoryId
        ),
        name:
          typeof product.name === "object"
            ? product.name
            : {
                ja: product.name || "",
                en: product.name_en || "",
                zh: product.name_zh || "",
              },
        price: product.price || 0,
        // BUG-005修正: 軽減税率対応のためtaxRateを追加
        taxRate: product.tax_rate || product.taxRate || 10,
        description:
          typeof product.description === "object"
            ? product.description
            : {
                ja: product.description || "",
                en: product.description_en || "",
                zh: product.description_zh || "",
              },
        imageUrl: product.image_url || product.imageUrl
          ? (product.image_url || product.imageUrl).startsWith('http')
            ? (product.image_url || product.imageUrl)
            : `${baseURL}${product.image_url || product.imageUrl}`
          : null,
        thumbnailUrl: product.thumbnail_url || product.thumbnailUrl || null,
        available: product.available !== false,
        popularity: product.popularity || 0,
      }));

      setProducts(processedProducts);
      setError(null);

      // 初回アニメーション実行後にフラグを設定（少し遅延させる）
      setTimeout(() => {
        hasAnimatedRef.current = true;
      }, 500);
    } catch (error) {
      console.error("メニューの読み込みに失敗しました:", error);
      setError("メニューの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderHistory = async () => {
    try {
      if (!sessionData) {
        return;
      }

      setHistoryLoading(true);

      const response = await api.get(`/api/orders`, {
        params: {
          sessionId: sessionData.sessionId,
          storeId: effectiveStoreId,
        },
      });

      if (response.data) {
        setOrderHistory(response.data);
      }
    } catch (error) {
      console.error("注文履歴の取得に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "注文履歴の取得に失敗しました",
        severity: "error",
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch recommendations when item is selected
  const fetchRecommendations = async (productId) => {
    try {
      // Get products from same category or popular items
      const currentProduct = products.find(p => p._id === productId);
      if (!currentProduct) return;

      const relatedProducts = products
        .filter(p => p._id !== productId && p.categoryId === currentProduct.categoryId)
        .slice(0, 3);

      // If not enough in same category, add popular items
      if (relatedProducts.length < 3) {
        const otherProducts = products
          .filter(p => p._id !== productId && !relatedProducts.includes(p))
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 3 - relatedProducts.length);
        relatedProducts.push(...otherProducts);
      }

      setRecommendations(relatedProducts);
    } catch (error) {
      console.error("レコメンド取得エラー:", error);
    }
  };

  useEffect(() => {
    if (sessionData) {
      fetchOrderHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData, storeId]);

  const handleItemClick = (item) => {
    setSelectedItem({
      ...item,
      _id: item._id || item.id,
    });
    setQuantity(1);
    setOrderNote("");
    setProductDialogOpen(true);
    fetchRecommendations(item._id || item.id);
    updateRecentlyViewed(item); // 最近見た商品に追加
  };

  // クイック追加ボタン（商品詳細を開かずにカートへ追加）
  const handleQuickAdd = (e, product) => {
    e.stopPropagation(); // カードクリックを防止
    const newCartItem = {
      _id: product._id,
      name: product.name,
      price: product.price,
      quantity: 1,
      note: "",
      imageUrl: product.imageUrl,
      categoryId: product.categoryId,
    };

    const existingItemIndex = cartItems.findIndex(
      (item) => item._id === newCartItem._id && item.note === ""
    );

    const updatedCartItems =
      existingItemIndex !== -1
        ? cartItems.map((item, index) =>
            index === existingItemIndex
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...cartItems, newCartItem];

    setCartItems(updatedCartItems);
    localStorage.setItem(`cart_${storeId}`, JSON.stringify(updatedCartItems));
    setSnackbar({
      open: true,
      message: `${getLocalizedName(product)} をカートに追加しました`,
      severity: "success",
    });
  };

  // 言語メニューハンドラ
  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    handleLanguageMenuClose();
  };

  const handleAddToCart = async () => {
    try {
      if (!selectedItem) return;

      const newCartItem = {
        _id: selectedItem._id,
        id: selectedItem.id || selectedItem._id,
        name: selectedItem.name,
        price: selectedItem.price,
        quantity: quantity,
        note: orderNote || "",
        imageUrl: selectedItem.imageUrl,
        categoryId: selectedItem.categoryId,
      };

      const existingItemIndex = cartItems.findIndex(
        (item) => item._id === newCartItem._id && item.note === newCartItem.note
      );

      const updatedCartItems =
        existingItemIndex !== -1
          ? cartItems.map((item, index) =>
              index === existingItemIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            )
          : [...cartItems, newCartItem];

      setCartItems(updatedCartItems);

      setProductDialogOpen(false);
      setSelectedItem(null);
      setQuantity(1);
      setOrderNote("");

      setSnackbar({
        open: true,
        message: "カートに追加しました",
        severity: "success",
      });

      localStorage.setItem(`cart_${storeId}`, JSON.stringify(updatedCartItems));
    } catch (error) {
      console.error("カートへの追加に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "カートへの追加に失敗しました",
        severity: "error",
      });
    }
  };

  const handleUpdateCartItem = (itemId, newQuantity, note) => {
    try {
      let updatedItems;
      if (newQuantity <= 0) {
        updatedItems = cartItems.filter(
          (item) => !(item._id === itemId && item.note === note)
        );
      } else {
        updatedItems = cartItems.map((item) =>
          item._id === itemId && item.note === note
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      setCartItems(updatedItems);
      localStorage.setItem(`cart_${effectiveStoreId}`, JSON.stringify(updatedItems));
    } catch (error) {
      console.error("カートの更新に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "カートの更新に失敗しました",
      });
    }
  };

  // UUID形式のバリデーション
  const isValidUUID = (str) => {
    if (!str || typeof str !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const handleOrder = async () => {
    // sessionIdはUUID形式必須（DBのsession_idカラムがUUID型のため）
    // 無効な形式の場合はnullにする
    const rawSessionId = sessionData?.sessionId;
    const sessionId = isValidUUID(rawSessionId) ? rawSessionId : null;
    const tableNumber = sessionData?.tableNumber || "1";
    const area = sessionData?.area || "テラス";

    if (cartItems.length === 0) {
      return;
    }

    try {
      const orderData = {
        sessionId: sessionId,
        storeId: effectiveStoreId,
        tableNumber: tableNumber,
        area: area,
        items: cartItems.map((item) => ({
          productId: item._id || item.id,
          name: getLocalizedName(item),
          quantity: item.quantity,
          price: item.price,
          note: item.note || "",
        })),
        totalAmount: cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        ),
        orderType: "MOBILE",
        paymentStatus: "UNPAID",
        paymentMethod: "CASH",
        status: "PENDING",
      };

      const response = await api.post(`/api/orders`, orderData);

      if (response.data) {
        // Show confetti celebration
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);

        setCartItems([]);
        setCartOpen(false);
        fetchOrderHistory();

        const orderId = response.data.id || response.data._id;
        navigate(`/store/${effectiveStoreId}/order-status/${orderId}`);

        setSnackbar({
          open: true,
          message: t("orderComplete"),
          severity: "success",
        });
      }
    } catch (error) {
      console.error("注文エラー:", error.response?.data || error.message || error);
      const errorMessage = error.response?.data?.message || t("orderError");
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleConfirmOrder = async () => {
    try {
      // sessionIdはUUID形式必須
      const rawSessionId = sessionData?.sessionId;
      const validSessionId = isValidUUID(rawSessionId) ? rawSessionId : null;

      const orderData = {
        sessionId: validSessionId,
        storeId: effectiveStoreId,
        tableNumber: sessionData?.tableNumber || "1",
        area: sessionData?.area || "",
        items: cartItems.map((item) => ({
          productId: item._id || item.id,
          name: getLocalizedName(item),
          quantity: item.quantity,
          price: item.price,
          note: item.note || "",
        })),
        totalAmount: cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        ),
        orderType: "MOBILE",
        paymentStatus: "UNPAID",
        paymentMethod: "CASH",
        status: "PENDING",
      };

      const response = await api.post(`/api/orders`, orderData);

      if (response.data) {
        // Show confetti celebration
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);

        setCartItems([]);
        setOrderDialogOpen(false);
        fetchOrderHistory();

        const orderId = response.data.id || response.data._id;
        navigate(`/store/${effectiveStoreId}/order-status/${orderId}`);

        setSnackbar({
          open: true,
          message: t("orderComplete"),
          severity: "success",
        });
      }
    } catch (error) {
      console.error("注文エラー:", error.response?.data || error.message || error);
      const errorMessage = error.response?.data?.message || t("orderError");
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return <LocalDiningIcon />;
    const name = (categoryName.ja || categoryName || "").toLowerCase();

    switch (name) {
      case "食事":
      case "定食":
      case "メイン":
      case "主菜":
        return <RestaurantIcon />;
      case "麺類":
      case "ラーメン":
      case "麺":
      case "うどん":
      case "そば":
        return <RamenDiningIcon />;
      case "ドリンク":
      case "飲み物":
      case "ドリンク類":
      case "お飲物":
        return <LocalBarIcon />;
      case "デザート":
      case "スイーツ":
      case "甘味":
      case "お菓子":
        return <IcecreamIcon />;
      case "サイド":
      case "一品料理":
      case "おつまみ":
      case "副菜":
        return <BakeryDiningIcon />;
      case "もんじゃ焼き":
      case "お好み焼き":
      case "海鮮焼き":
        return <FireIcon sx={{ color: '#ff6b00' }} />;
      default:
        return <LocalDiningIcon />;
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "0";
    return price.toLocaleString();
  };

  const getLocalizedName = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;

    const currentLang = i18n.language;

    if (item.name && typeof item.name === "object") {
      return (
        item.name[currentLang] ||
        item.name.ja ||
        item.name.en ||
        item.name.zh ||
        ""
      );
    }

    return item[currentLang] || item.ja || item.en || item.zh || "";
  };


  useEffect(() => {
    localStorage.setItem("preferred_language", i18n.language);
  }, [i18n.language]);

  const handleCartOpen = () => {
    setCartOpen(true);
  };

  const handleCartClose = () => {
    setCartOpen(false);
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "PREPARING":
        return "info";
      case "READY":
        return "success";
      case "DELIVERED":
        return "primary";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getOrderStatusText = (status) => {
    switch (status) {
      case "PENDING":
        return "受付待ち";
      case "PREPARING":
        return "調理中 🔥";
      case "READY":
        return "できあがり ✨";
      case "DELIVERED":
        return "配達済み";
      case "CANCELLED":
        return "キャンセル";
      default:
        return "不明";
    }
  };

  const getOrderStatusStep = (status) => {
    switch (status) {
      case "PENDING":
        return 0;
      case "PREPARING":
        return 1;
      case "READY":
        return 2;
      case "DELIVERED":
        return 3;
      default:
        return 0;
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleCallStaff = async () => {
    try {
      await api.post(`/api/session/${sessionData.sessionId}/call-staff`, {
        tableNumber: sessionData.tableNumber,
        area: sessionData.area,
        reason: callReason,
      });
      setCallStaffDialogOpen(false);
      setCallReason("");
      setSnackbar({
        open: true,
        message: t("staffCallSuccess"),
        severity: "success",
      });
    } catch (error) {
      console.error("店員呼び出しエラー:", error);
      setSnackbar({
        open: true,
        message: t("staffCallError"),
        severity: "error",
      });
    }
  };

  // Enhanced ProductCard with motion (#4-14 改善適用)
  // memo化でカート操作時の不要な再レンダリングを防止
  const ProductCard = memo(({ product, index, skipAnimation }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const isPopular = product.popularity >= 5 || product.isPopular;
    const description = getLocalizedDescription(product);

    // Placeholder image based on store type and category (multi-tenant support)
    const getPlaceholderImage = () => {
      const category = categories.find(c => (c._id || c.id) === product.categoryId);
      const categoryName = category?.name?.ja || category?.name || '';
      const storeType = store?.store_type || store?.storeType || 'general';
      return getPlaceholderImageForCategory(storeType, categoryName);
    };

    const imageUrl = imageError || !product.imageUrl
      ? getPlaceholderImage()
      : product.imageUrl;

    return (
      <MotionCard
        initial={skipAnimation ? false : { opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={skipAnimation ? { duration: 0 } : { duration: 0.15, delay: Math.min(index * 0.02, 0.2) }}
        whileHover={{
          scale: 1.02,
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          transition: { duration: 0.15 }
        }}
        whileTap={{ scale: 0.97 }}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: 2,
          position: 'relative',
          bgcolor: 'background.paper',
        }}
        data-testid="product-card"
        onClick={() => handleItemClick(product)}
      >
        {/* #8: 固定アスペクト比 4:3 */}
        <Box sx={{ position: 'relative', paddingTop: '75%' }}>
          {/* Loading skeleton (#15) */}
          {!imageLoaded && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              animation="wave"
            />
          )}
          <CardMedia
            component="img"
            image={imageUrl}
            alt={getLocalizedName(product)}
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: !product.available ? 'grayscale(100%) brightness(0.7)' : 'none',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          />

          {/* #12: 人気商品バッジ */}
          {isPopular && product.available && (
            <Chip
              icon={<StarIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
              label="人気"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: '#ff6b00',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
          )}

          {/* BUG-005修正: 軽減税率バッジ（8%対象商品） */}
          {product.taxRate === 8 && product.available && (
            <Chip
              label="軽減税率"
              size="small"
              sx={{
                position: 'absolute',
                top: isPopular ? 36 : 8,
                left: 8,
                bgcolor: '#4caf50',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          )}

          {/* #11: 売り切れ表示の強化 */}
          {!product.available && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                売り切れ
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                SOLD OUT
              </Typography>
            </Box>
          )}

          {/* #5: クイック追加ボタン */}
          {product.available && (
            <Tooltip title="カートに追加" placement="left">
              <IconButton
                onClick={(e) => handleQuickAdd(e, product)}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 36,
                  height: 36,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.15s ease',
                }}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* #4 & #10: 商品情報（説明追加 & 価格を下部に配置） */}
        <CardContent sx={{ flexGrow: 1, p: 1.5, pb: '12px !important' }}>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {getLocalizedName(product)}
          </Typography>

          {/* #4: 商品説明（短縮版） */}
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 0.5,
                lineHeight: 1.3,
              }}
            >
              {description}
            </Typography>
          )}

          {/* #10: 価格を右下に配置 */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mt: 'auto',
            }}
          >
            ¥{formatPrice(product.price)}
          </Typography>
        </CardContent>
      </MotionCard>
    );
  });

  // Enhanced Product Dialog with sizzle effect
  const ProductDialog = () => {
    const [dialogImageError, setDialogImageError] = useState(false);

    const getPlaceholderImage = () => {
      if (!selectedItem) return '';
      const category = categories.find(c => (c._id || c.id) === selectedItem.categoryId);
      const categoryName = category?.name?.ja || category?.name || '';
      const storeType = store?.store_type || store?.storeType || 'general';
      // Use larger image for dialog
      return getPlaceholderImageForCategory(storeType, categoryName).replace('w=600&h=400', 'w=800&h=600');
    };

    const imageUrl = dialogImageError || !selectedItem?.imageUrl
      ? getPlaceholderImage()
      : selectedItem.imageUrl;

    return (
      <Dialog
        open={productDialogOpen}
        onClose={() => {
          setProductDialogOpen(false);
          setDialogImageError(false);
        }}
        fullScreen
        TransitionComponent={SlideTransition}
        data-testid="add-to-cart-modal"
      >
        <Box sx={{ position: 'relative', height: '100%', bgcolor: 'background.default' }}>
          {/* Close button */}
          <IconButton
            onClick={() => setProductDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Hero image with sizzle effect */}
          <Box sx={{ position: 'relative', height: '40vh', overflow: 'hidden' }}>
            <CardMedia
              component="img"
              height="100%"
              image={imageUrl}
              alt={getLocalizedName(selectedItem?.name)}
              onError={() => setDialogImageError(true)}
              sx={{
                objectFit: 'cover',
                filter: 'brightness(0.9)',
              }}
            />
            {/* Gradient overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '60%',
                background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              }}
            />
            {/* Product name on image */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 24,
                left: 24,
                right: 24,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  mb: 1,
                }}
              >
                {getLocalizedName(selectedItem?.name)}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#ffd700',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                ¥{formatPrice(selectedItem?.price)}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {/* Description */}
            {selectedItem?.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {getLocalizedName(selectedItem?.description)}
              </Typography>
            )}

            {/* Quantity selector */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                {t("quantity")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                  sx={{ bgcolor: 'white' }}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography variant="h5" sx={{ minWidth: 40, textAlign: 'center', fontWeight: 700 }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity(quantity + 1)}
                  sx={{ bgcolor: 'white' }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Paper>

            {/* Note input */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t("note")}
              placeholder="アレルギーや調理に関するご要望をお書きください"
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  🍴 よく一緒に注文されています
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                  {recommendations.map((rec) => (
                    <Chip
                      key={rec._id}
                      label={`${getLocalizedName(rec)} ¥${formatPrice(rec.price)}`}
                      onClick={() => {
                        setSelectedItem(rec);
                        setQuantity(1);
                        fetchRecommendations(rec._id);
                      }}
                      sx={{
                        flexShrink: 0,
                        '&:hover': { bgcolor: 'primary.light', color: 'white' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Subtotal and Add button */}
            <Box sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: 'white',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">小計</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ¥{formatPrice((selectedItem?.price || 0) * quantity)}
                </Typography>
              </Box>
              <Button
                onClick={handleAddToCart}
                variant="contained"
                fullWidth
                size="large"
                data-testid="add-to-cart-button"
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                {t("addToCart")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    );
  };

  // Cart Items with motion
  const CartItems = () => (
    <List>
      <AnimatePresence>
        {cartItems.map((item, index) => (
          <motion.div
            key={`${item._id}-${item.note}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
          >
            <ListItem>
              <ListItemText
                primary={getLocalizedName(item)}
                secondary={`${t("total")}: ¥${formatPrice(item.price * item.quantity)}`}
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => handleUpdateCartItem(item._id, item.quantity - 1, item.note)}
                >
                  <RemoveIcon data-testid="RemoveIcon" />
                </IconButton>
                <Typography sx={{ mx: 1, minWidth: 24, textAlign: 'center' }} data-testid="cart-item-quantity">
                  {item.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleUpdateCartItem(item._id, item.quantity + 1, item.note)}
                >
                  <AddIcon data-testid="AddIcon" />
                </IconButton>
              </Box>
            </ListItem>
          </motion.div>
        ))}
      </AnimatePresence>
    </List>
  );

  // Order status with stepper
  const OrderStatusStepper = ({ status }) => (
    <Stepper activeStep={getOrderStatusStep(status)} orientation="vertical" sx={{ mt: 2 }}>
      <Step>
        <StepLabel StepIconComponent={() => <TimeIcon color={status === 'PENDING' ? 'warning' : 'success'} />}>
          注文受付
        </StepLabel>
      </Step>
      <Step>
        <StepLabel StepIconComponent={() => <FireIcon color={status === 'PREPARING' ? 'warning' : (getOrderStatusStep(status) > 1 ? 'success' : 'disabled')} />}>
          調理中 🔥
        </StepLabel>
      </Step>
      <Step>
        <StepLabel StepIconComponent={() => <CheckCircleIcon color={status === 'READY' || status === 'DELIVERED' ? 'success' : 'disabled'} />}>
          できあがり ✨
        </StepLabel>
      </Step>
    </Stepper>
  );

  // Order confirm dialog
  const OrderConfirmDialog = () => (
    <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)}>
      <DialogTitle>{t("confirmOrder")}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {t("orderConfirmMessage")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("orderConfirmNote")}
        </Typography>
        <List>
          {cartItems.map((item, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={getLocalizedName(item)}
                secondary={`${item.quantity}x ¥${formatPrice(item.price)}`}
              />
            </ListItem>
          ))}
          <Divider />
          <ListItem>
            <ListItemText
              primary={t("total")}
              secondary={`¥${formatPrice(
                cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
              )}`}
            />
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOrderDialogOpen(false)}>{t("cancel")}</Button>
        <Button onClick={handleConfirmOrder} color="primary" data-testid="place-order-button">
          {t("confirmCashPayment")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">メニューを読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" role="alert" data-testid="error-message">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!store) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">店舗情報が見つかりません</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={storeTheme}>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {/* Confetti animation */}
        <Confetti show={showConfetti} />

        {/* #1-3: 軽量化されたヘッダー */}
        <AppBar
          position="fixed"
          sx={{
            top: 0,
            bgcolor: store.primaryColor || 'primary.main',
            borderBottom: 'none',
            zIndex: 1100,
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", minHeight: { xs: 48, sm: 56 }, py: 0.5 }}>
            {/* 左: ロゴと店舗名（コンパクト化） */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {store.logoUrl && (
                <Box
                  component="img"
                  src={store.logoUrl}
                  alt={store.name}
                  sx={{ height: { xs: 28, sm: 36 }, borderRadius: 1 }}
                />
              )}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                  maxWidth: { xs: 120, sm: 200 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {store?.name || ""}
              </Typography>
            </Box>

            {/* 右: アクションボタン（モバイルでは重複を削除） */}
            <Box sx={{ display: "flex", alignItems: 'center', gap: 0.5 }}>
              {/* #17: 検索トグル */}
              <IconButton
                color="inherit"
                onClick={() => setShowSearch(!showSearch)}
                size="small"
                sx={{ display: { xs: 'inline-flex', sm: 'inline-flex' } }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>

              {/* #3: モバイル対応言語切替 */}
              <IconButton
                color="inherit"
                onClick={handleLanguageMenuOpen}
                size="small"
                aria-label="言語切替"
              >
                <LanguageIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={languageMenuAnchor}
                open={Boolean(languageMenuAnchor)}
                onClose={handleLanguageMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => handleLanguageSelect('ja')}
                  selected={i18n.language === 'ja'}
                  sx={{ minWidth: 120 }}
                >
                  🇯🇵 日本語
                </MenuItem>
                <MenuItem
                  onClick={() => handleLanguageSelect('en')}
                  selected={i18n.language === 'en'}
                >
                  🇺🇸 English
                </MenuItem>
                <MenuItem
                  onClick={() => handleLanguageSelect('zh')}
                  selected={i18n.language === 'zh'}
                >
                  🇨🇳 中文
                </MenuItem>
              </Menu>

              {/* #2: デスクトップのみ履歴ボタン表示 */}
              <IconButton
                color="inherit"
                onClick={() => setHistoryOpen(true)}
                aria-label={t("orderHistory")}
                size="small"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>

              {/* #6: カートバッジ（総数量表示） */}
              <Tooltip title={`カート: ${cartTotalItems}点 ¥${formatPrice(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0))}`}>
                <IconButton
                  color="inherit"
                  onClick={handleCartOpen}
                  aria-label={t("cart")}
                  data-testid="cart-button"
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <Badge
                    badgeContent={cartTotalItems}
                    color="error"
                    data-testid="cart-badge"
                    max={99}
                  >
                    <ShoppingCartIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>

          {/* #17: 検索バー（折りたたみ式） */}
          <Collapse in={showSearch}>
            <Box sx={{ px: 2, pb: 1, bgcolor: 'rgba(0,0,0,0.1)' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="商品を検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <CloseIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&::placeholder': { color: 'rgba(255,255,255,0.5)' },
                  },
                }}
                sx={{
                  '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
                  '& input': { color: 'white' },
                }}
              />
            </Box>
          </Collapse>
        </AppBar>

        {/* #8: カテゴリタブを分離（スティッキー） */}
        <Box
          sx={{
            position: 'fixed',
            top: `calc(${showSearch ? '100px' : '48px'} + env(safe-area-inset-top, 0px))`,
            left: 0,
            right: 0,
            zIndex: 1099,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'top 0.2s ease',
          }}
          data-testid="category-list"
        >
          {/* #16: プルダウンリフレッシュインジケーター */}
          {isRefreshing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <Tabs
            value={selectedCategory}
            onChange={(e, newValue) => setSelectedCategory(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                py: 1,
                px: 2,
                fontWeight: 600,
                fontSize: '0.85rem',
                // #14: コントラスト改善
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab label={t("allCategories")} value="all" data-testid="category-tab-all" />
            {categories.map((category) => {
              const categoryId = category._id || category.id;
              const categoryName = getLocalizedName(category);
              return (
                <Tab
                  key={categoryId}
                  label={categoryName}
                  value={categoryId}
                  icon={getCategoryIcon(category.name)}
                  iconPosition="start"
                  data-testid={`category-tab-${categoryId}`}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // ヘッダー(48px) + タブ(48px) + セーフエリア + 検索バー分
            mt: `calc(${showSearch ? '148px' : '96px'} + env(safe-area-inset-top, 0px))`,
            px: { xs: 1, sm: 2 },
            pb: { xs: 10, sm: 4 },
            transition: 'margin-top 0.2s ease',
          }}
        >
          {/* #18: 最近見た商品セクション */}
          {recentlyViewed.length > 0 && selectedCategory === 'all' && !searchQuery && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, px: 0.5 }}>
                最近見た商品
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                {recentlyViewed.map((product) => (
                  <Chip
                    key={product._id}
                    label={getLocalizedName(product)}
                    onClick={() => handleItemClick(product)}
                    onDelete={(e) => {
                      e.stopPropagation();
                      handleQuickAdd(e, product);
                    }}
                    deleteIcon={<AddIcon fontSize="small" />}
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box data-testid="product-list">
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={product._id}>
                    <ProductCard product={product} index={index} skipAnimation={hasAnimatedRef.current} />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      {searchQuery ? `「${searchQuery}」に一致する商品がありません` : t("noProductsFound")}
                    </Typography>
                    {searchQuery && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{ mt: 1 }}
                      >
                        検索をクリア
                      </Button>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>

        {/* Mobile bottom navigation with cart summary (#2, #6, #19 改善) */}
        <AppBar
          position="fixed"
          color="primary"
          sx={{
            top: "auto",
            bottom: 0,
            bgcolor: store.primaryColor || "primary.main",
            display: { xs: "block", sm: "none" },
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, px: 2 }}>
            {/* 履歴ボタン */}
            <IconButton color="inherit" onClick={() => setHistoryOpen(true)} aria-label={t("orderHistory")}>
              <HistoryIcon />
            </IconButton>

            {/* #19: カートサマリー（プレビュー機能付き） */}
            {cartItems.length > 0 ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                style={{ flex: 1, marginLeft: 8, marginRight: 8 }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCartOpen}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    py: 1,
                    borderRadius: 3,
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'grey.100' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cartTotalItems}点
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      ¥{formatPrice(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0))}
                    </Typography>
                  </Box>
                </Button>
              </motion.div>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}

            {/* カートボタン */}
            <IconButton color="inherit" onClick={handleCartOpen} aria-label={t("cart")} data-testid="cart-button">
              <Badge badgeContent={cartTotalItems} color="error" data-testid="cart-badge" max={99}>
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Order history drawer */}
        <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
          <Box sx={{ width: { xs: 300, sm: 360 }, p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t("orderHistory")}
            </Typography>

            {historyLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
                {[1, 2, 3].map((item) => (
                  <Box key={item} sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}>
                    <Box sx={{ bgcolor: "grey.200", height: 20, width: "70%", mb: 1, borderRadius: 1, animation: 'pulse 1.5s infinite' }} />
                    <Box sx={{ bgcolor: "grey.200", height: 15, width: "40%", borderRadius: 1, animation: 'pulse 1.5s infinite' }} />
                  </Box>
                ))}
              </Box>
            ) : orderHistory.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">{t("noOrderHistory")}</Typography>
              </Box>
            ) : (
              <List>
                {orderHistory.map((order) => (
                  <React.Fragment key={order._id}>
                    <ListItem
                      button
                      onClick={() => handleOrderClick(order)}
                      sx={{ borderRadius: 2, mb: 1, bgcolor: 'grey.50' }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle2">
                              {t("orderNumber")}: {order._id.slice(-6)}
                            </Typography>
                            <Chip size="small" label={getOrderStatusText(order.status)} color={getOrderStatusColor(order.status)} />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2">
                              {t("orderDate")}: {new Date(order.createdAt).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t("total")}: ¥{formatPrice(order.totalAmount)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Drawer>

        {/* Order details dialog with stepper */}
        <Dialog open={orderDetailsOpen} onClose={() => setOrderDetailsOpen(false)} maxWidth="sm" fullWidth>
          {selectedOrder && (
            <>
              <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  注文詳細
                  <Chip size="small" label={getOrderStatusText(selectedOrder.status)} color={getOrderStatusColor(selectedOrder.status)} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Typography variant="subtitle2" gutterBottom>
                  注文番号: {selectedOrder._id}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  注文日時: {new Date(selectedOrder.createdAt).toLocaleString()}
                </Typography>

                {/* Order status stepper */}
                <OrderStatusStepper status={selectedOrder.status} />

                <Divider sx={{ my: 2 }} />
                <List>
                  {selectedOrder.items.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={item.name}
                        secondary={`¥${formatPrice(item.price)} × ${item.quantity}`}
                      />
                      <Typography variant="body2">
                        ¥{formatPrice(item.price * item.quantity)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" align="right" sx={{ fontWeight: 700 }}>
                  合計: ¥{formatPrice(selectedOrder.totalAmount)}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOrderDetailsOpen(false)}>閉じる</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Dialogs */}
        <OrderConfirmDialog />
        <ProductDialog />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity || "success"} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Cart drawer */}
        <Drawer anchor="right" open={cartOpen} onClose={handleCartClose}>
          <Box sx={{ width: { xs: 320, sm: 380 }, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("cart")} ({cartTotalItems}点)
              </Typography>
              <IconButton onClick={handleCartClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            {cartItems.length === 0 ? (
              // #13: 空カートのCTA強化
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, textAlign: 'center', px: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 80, color: 'grey.200' }} />
                <Typography variant="h6" color="text.secondary" data-testid="empty-cart-message">
                  カートは空です
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  メニューから商品を追加してください
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleCartClose}
                  startIcon={<RestaurantIcon />}
                  sx={{ borderRadius: 3 }}
                >
                  メニューを見る
                </Button>
                {recentlyViewed.length > 0 && (
                  <Box sx={{ mt: 3, width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      最近見た商品
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {recentlyViewed.slice(0, 3).map((product) => (
                        <Chip
                          key={product._id}
                          label={getLocalizedName(product)}
                          size="small"
                          onClick={() => {
                            handleCartClose();
                            handleItemClick(product);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <CartItems />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>小計</Typography>
                    <Typography>¥{formatPrice(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{t("total")}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      ¥{formatPrice(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0))}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleOrder}
                  data-testid="place-order-button"
                  sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 2 }}
                >
                  {t("order")}
                </Button>
              </>
            )}
          </Box>
        </Drawer>
      </Box>
    </ThemeProvider>
  );
};

export default StoreMenu;
